import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataTablePagination } from "@/components/ClientsheetTable/data-table-pagination";
import { DataTableToolbar } from "@/components/InvoiceTable/data-table-toolbar";
import type { InvoicePublic } from "./schema";

interface DataTableProps<TValue> {
  columns: ColumnDef<InvoicePublic, TValue>[];
  data: InvoicePublic[];
  isLoading: boolean;
}

interface InvoiceData {
  data?: InvoicePublic[];
}

export function DataTable<TValue>({ columns, data, isLoading }: DataTableProps<TValue>) {
  const { t } = useTranslation();
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]); // For filename filter
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const [invoiceType, setInvoiceType] = React.useState<string | null>(null);
  const [month, setMonth] = React.useState<number | null>(null);
  const [year, setYear] = React.useState<number | null>(null);

  const filteredData = React.useMemo(() => {
    if (!data) return [];
    return data.filter((invoice) => {
      const typeMatch = invoiceType ? invoice.type === invoiceType : true;
      const monthMatch = month ? invoice.month === month : true;
      const yearMatch = year ? invoice.year === year : true;
      return typeMatch && monthMatch && yearMatch;
    });
  }, [data, invoiceType, month, year]);

  const invoiceDataForComboboxes: InvoiceData = React.useMemo(() => ({ data }), [data]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return (
    <div className="space-y-4">
      <DataTableToolbar
        table={table}
        invoiceData={invoiceDataForComboboxes}
        isLoading={isLoading}
        invoiceType={invoiceType}
        setInvoiceType={setInvoiceType}
        month={month}
        setMonth={setMonth}
        year={year}
        setYear={setYear}
      />
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {t("No results.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} />
    </div>
  );
}
